// /home/bigbrain/projects/smashparty/src/client/ui/components/button.ts
/**
 * Plan 23a, Task 2.1 — Button primitive
 *
 * Factory: createButton(config: UIComponentConfig & ButtonOpts): UIComponentHandle
 *
 * Supported variants: primary | play | secondary | success | danger | accent | ghost
 * Supported sizes:    default | large | small | icon
 *
 * Integrates Plan 20a button-states hook for hover / pressed / released / gamepad-focus.
 * Does NOT call gsap.timeline() directly — all GSAP usage flows through the hook.
 *
 * Design system references:
 *   - docs/design/tokens.json  — color, typography, spacing, shadow values
 *   - docs/design/components.md §1  — Button construction + variant/size tables
 */

import { createButtonStatesHook } from '../vfx/button-states';
import type { UIComponentHandle, UIComponentConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ButtonVariant =
  | 'primary'
  | 'play'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'accent'
  | 'ghost';
export type ButtonSize = 'default' | 'large' | 'small' | 'icon';

export interface ButtonOpts {
  /** Button variant — maps to color.button.<variant>.* in tokens.json. Default: 'primary'. */
  variant?: ButtonVariant;
  /** Size preset — controls padding, min-height, font-size, border-radius. Default: 'default'. */
  size?: ButtonSize;
  /** Button label text — rendered ALL-CAPS via text-transform. */
  label: string;
  /** When true, button is visually dimmed (opacity 0.55) and non-interactive. */
  disabled?: boolean;
  /** Callback fired on click (not fired when disabled). */
  onClick?: () => void;
  /** When true, the button-states gamepad-focus animation is started immediately. */
  gamepadFocused?: boolean;
}

let buttonClickSfxHandler: (() => void) | null = null;
let buttonHoverSfxHandler: (() => void) | null = null;

export function setButtonClickSfxHandler(handler: (() => void) | null): void {
  buttonClickSfxHandler = handler;
}

export function setButtonHoverSfxHandler(handler: (() => void) | null): void {
  buttonHoverSfxHandler = handler;
}

function playButtonClickSfx(): void {
  try {
    buttonClickSfxHandler?.();
  } catch (err) {
    console.warn('[ui-button] click SFX handler failed:', err);
  }
}

function playButtonHoverSfx(): void {
  try {
    buttonHoverSfxHandler?.();
  } catch (err) {
    console.warn('[ui-button] hover SFX handler failed:', err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BUTTON_VARIANTS — Record<ButtonVariant, token CSS-var lookup strings>
// Fallback values MUST exactly match tokens.json.
// Ghost variant special-cased: transparent background, no gradient.
// ─────────────────────────────────────────────────────────────────────────────

export interface ButtonVariantTokens {
  gradTop: string;
  gradBottom: string;
  border: string;
  text: string;
}

// Ghost: transparent background, white-alpha border, no bottom shadow. hover-bg wired in onMouseEnter.
// prettier-ignore
export const BUTTON_VARIANTS: Record<ButtonVariant, ButtonVariantTokens> = {
  primary:   { gradTop: 'var(--color-button-primary-gradient-top, #F0B838)',   gradBottom: 'var(--color-button-primary-gradient-bottom, #C4841C)',   border: 'var(--color-button-primary-border, #8A5A10)',   text: 'var(--color-button-primary-text, #FFFFFF)' },
  play:      { gradTop: 'var(--color-button-play-gradient-top, #6CC050)',      gradBottom: 'var(--color-button-play-gradient-bottom, #488A30)',      border: 'var(--color-button-play-border, #2E5E1C)',      text: 'var(--color-button-play-text, #FFFFFF)' },
  secondary: { gradTop: 'var(--color-button-secondary-gradient-top, #5CA0E8)', gradBottom: 'var(--color-button-secondary-gradient-bottom, #3A6FB0)', border: 'var(--color-button-secondary-border, #264A78)', text: 'var(--color-button-secondary-text, #FFFFFF)' },
  success:   { gradTop: 'var(--color-button-success-gradient-top, #6CC050)',   gradBottom: 'var(--color-button-success-gradient-bottom, #488A30)',   border: 'var(--color-button-success-border, #2E5E1C)',   text: 'var(--color-button-success-text, #FFFFFF)' },
  danger:    { gradTop: 'var(--color-button-danger-gradient-top, #E85555)',    gradBottom: 'var(--color-button-danger-gradient-bottom, #B03030)',    border: 'var(--color-button-danger-border, #7A1C1C)',    text: 'var(--color-button-danger-text, #FFFFFF)' },
  accent:    { gradTop: 'var(--color-button-accent-gradient-top, #F0A040)',    gradBottom: 'var(--color-button-accent-gradient-bottom, #C07020)',    border: 'var(--color-button-accent-border, #8A5018)',    text: 'var(--color-button-accent-text, #FFFFFF)' },
  ghost:     { gradTop: 'transparent',                                         gradBottom: 'transparent',                                           border: 'var(--color-button-ghost-border, rgba(255,255,255,0.30))', text: 'var(--color-button-ghost-text, #FFFFFF)' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Size → CSS value maps
// ─────────────────────────────────────────────────────────────────────────────

interface ButtonSizeTokens {
  fontSize: string;
  padding: string;
  minHeight: string;
  borderRadius: string;
}

const BUTTON_SIZES: Record<ButtonSize, ButtonSizeTokens> = {
  default: {
    fontSize: 'var(--typography-size-h5,      18px)',
    padding: 'var(--layout-button-padding,   12px 24px)',
    minHeight: 'var(--layout-button-min-height, 48px)',
    borderRadius: 'var(--border-radius-lg,        12px)',
  },
  large: {
    fontSize: 'var(--typography-size-h4,         22px)',
    padding: 'var(--layout-button-padding-lg,   16px 32px)',
    minHeight: '56px',
    borderRadius: 'var(--border-radius-xl,           16px)',
  },
  small: {
    fontSize: 'var(--typography-size-caption, 14px)',
    padding: '10px 16px',
    minHeight: '48px',
    borderRadius: 'var(--border-radius-md,        8px)',
  },
  icon: {
    fontSize: 'inherit',
    padding: '10px',
    minHeight: '48px',
    borderRadius: 'var(--border-radius-md, 8px)',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createButton(config: UIComponentConfig & ButtonOpts): UIComponentHandle {
  const {
    gsapRegistry,
    mount,
    label,
    variant = 'primary',
    size = 'default',
    disabled = false,
    onClick,
    gamepadFocused = false,
  } = config;

  const tokens = BUTTON_VARIANTS[variant];
  const sizeTokens = BUTTON_SIZES[size];
  const isGhost = variant === 'ghost';

  // ── Create DOM ────────────────────────────────────────────────────────────

  const button = document.createElement('button');
  button.className = `ui-button ui-button--${variant} ui-button--${size}`;
  button.type = 'button';

  // ── Inner label span ──────────────────────────────────────────────────────

  const span = document.createElement('span');
  span.textContent = label.toUpperCase();

  // Span style — label typography
  span.style.cssText = [
    `font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)`,
    `font-size: ${sizeTokens.fontSize}`,
    `font-weight: var(--typography-weight-bold, 700)`,
    `letter-spacing: var(--typography-letter-spacing-headline, 1px)`,
    `text-shadow: var(--typography-shadow-heading, 0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60))`,
    `color: ${tokens.text}`,
    `text-transform: uppercase`,
    `pointer-events: ${disabled ? 'none' : 'auto'}`,
    `display: block`,
    `width: 100%`,
    `text-align: center`,
    `user-select: none`,
  ].join('; ');

  button.appendChild(span);

  // ── Button element style ──────────────────────────────────────────────────

  const background = isGhost
    ? 'transparent'
    : `linear-gradient(to bottom, ${tokens.gradTop}, ${tokens.gradBottom})`;

  const boxShadow = isGhost ? 'none' : `var(--shadow-button, 0 4px 0 rgba(0,0,0,0.50))`;

  button.style.cssText = [
    `display: inline-flex`,
    `align-items: center`,
    `justify-content: center`,
    `cursor: ${disabled ? 'not-allowed' : 'pointer'}`,
    `border: 2px solid ${tokens.border}`,
    `border-radius: ${sizeTokens.borderRadius}`,
    `padding: ${sizeTokens.padding}`,
    `min-height: ${sizeTokens.minHeight}`,
    `background: ${background}`,
    `box-shadow: ${boxShadow}`,
    `opacity: ${disabled ? '0.55' : '1'}`,
    `transition: filter var(--animation-duration-fast, 150ms) var(--animation-easing-snappy, cubic-bezier(0.2,0,0,1)),` +
      ` box-shadow var(--animation-duration-fast, 150ms) var(--animation-easing-snappy, cubic-bezier(0.2,0,0,1))`,
    `outline: none`,
    `text-decoration: none`,
    `box-sizing: border-box`,
    `position: relative`,
    `overflow: hidden`,
    `touch-action: manipulation`,
    `-webkit-tap-highlight-color: transparent`,
  ].join('; ');

  if (disabled) {
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('disabled', '');
  }

  // ── Plan 20a hook ──────────────────────────────────────────────────────────

  const hook = createButtonStatesHook({ gsapRegistry });
  let disposed = false;

  // ── Event listeners ────────────────────────────────────────────────────────

  const GHOST_HOVER_BG = 'var(--color-button-ghost-hover-bg, rgba(255,255,255,0.10))';

  function onMouseEnter(): void {
    if (disabled) return;
    hook.play(button, { kind: 'hover' });
    playButtonHoverSfx();
    if (isGhost) {
      button.style.background = GHOST_HOVER_BG;
      button.dataset.ghostHover = '1';
    }
  }

  function onMouseLeave(): void {
    if (disabled) return;
    // Kill the hover animation — no explicit 'idle' kind; killing resets to default.
    hook.play(button, { kind: 'released' });
    if (isGhost) {
      button.style.background = 'transparent';
      delete button.dataset.ghostHover;
    }
  }

  function onMouseDown(): void {
    if (disabled) return;
    hook.play(button, { kind: 'pressed' });
  }

  function onMouseUp(): void {
    if (disabled) return;
    hook.play(button, { kind: 'released' });
  }

  function onFocus(): void {
    if (disabled) return;
    hook.play(button, { kind: 'hover' });
  }

  function onBlur(): void {
    if (disabled) return;
    hook.play(button, { kind: 'released' });
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      hook.play(button, { kind: 'pressed' });
    }
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      hook.play(button, { kind: 'released' });
      playButtonClickSfx();
      if (onClick) onClick();
    }
  }

  function onClickHandler(): void {
    if (disabled) return;
    playButtonClickSfx();
    if (onClick) onClick();
  }

  button.addEventListener('mouseenter', onMouseEnter);
  button.addEventListener('mouseleave', onMouseLeave);
  button.addEventListener('mousedown', onMouseDown);
  button.addEventListener('mouseup', onMouseUp);
  button.addEventListener('focus', onFocus);
  button.addEventListener('blur', onBlur);
  button.addEventListener('keydown', onKeyDown as EventListener);
  button.addEventListener('keyup', onKeyUp as EventListener);
  button.addEventListener('click', onClickHandler);

  // ── Gamepad focus pulse (start immediately if configured) ──────────────────

  if (gamepadFocused) {
    hook.play(button, { kind: 'gamepad-focus' });
  }

  // ── Mount ──────────────────────────────────────────────────────────────────

  if (mount) {
    mount.appendChild(button);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove all event listeners
    button.removeEventListener('mouseenter', onMouseEnter);
    button.removeEventListener('mouseleave', onMouseLeave);
    button.removeEventListener('mousedown', onMouseDown);
    button.removeEventListener('mouseup', onMouseUp);
    button.removeEventListener('focus', onFocus);
    button.removeEventListener('blur', onBlur);
    button.removeEventListener('keydown', onKeyDown as EventListener);
    button.removeEventListener('keyup', onKeyUp as EventListener);
    button.removeEventListener('click', onClickHandler);

    // Kill in-flight gsap timelines via hook
    hook.dispose();

    // Detach from parent
    if (button.parentNode) {
      button.parentNode.removeChild(button);
    }
  }

  return { element: button, dispose };
}
